import React, { useEffect, useState, useCallback, useRef } from 'react';
import { trackEvent } from '../../analytics';
import { trialUrlResolvesToBlockedSlug } from '../../data/blockedTrialSlugs.js';
import { TRIAL_URL, withTrialPromo } from '../../data/pricing';
import { OPEN_TRIAL_SCHEDULER_EVENT } from './trialSchedulerEvents';

// ─────────────────────────────────────────────────────────────────────────────
// TRIAL_SCHEDULER_MODE — modal vs new-tab test 2026-07-12→07-18: ended early —
// the "~0 modal-era bookings" premise was an ads-tracking artifact (Opus1 logs
// show 32 self-serve bookings Apr 15–Jul 11, ~2.7/wk; conversion uploader live
// only since Jul 12). Modal restored per owner.
// Full data: PKA memory `project_sflcm_landing_pages`.
export const TRIAL_SCHEDULER_MODE = 'modal'; // 'modal' | 'newtab'
// ─────────────────────────────────────────────────────────────────────────────

const MATCHERS = [
  { kind: 'trial', test: (u) => u.includes('sflcm.opus1.io/w/trial'), title: 'Book a Trial Lesson', event: 'trial_modal_open' },
  { kind: 'portal', test: (u) => u.includes('app.opus1.io') || (u.includes('sflcm.opus1.io') && !u.includes('/w/')), title: 'Student Portal', event: 'portal_modal_open' },
];

function matchUrl(url) {
  return MATCHERS.find((m) => m.test(url));
}

// Same construction the query-param effect has always used. The blocked-slug
// check runs on this final URL (normalized path), not on the raw param.
function builtTrialUrl(trial) {
  const slug = trial === 'open' || trial === '1' || trial === 'true' ? '' : `-${trial}`;
  return withTrialPromo(`https://sflcm.opus1.io/w/trial${slug}`);
}

export function resolveTrialQueryUrl(trialValues) {
  const values = (Array.isArray(trialValues) ? trialValues : [trialValues]).filter(Boolean);
  if (!values.length) return null;
  // Build every value the same way. A blocked final path uses the generic
  // trial URL. Any other slug keeps the first URL built above.
  const built = values.map(builtTrialUrl);
  if (built.some((url) => trialUrlResolvesToBlockedSlug(url))) return TRIAL_URL;
  return built[0];
}

export default function TrialSchedulerModal() {
  const [state, setState] = useState(null); // { url, title }
  const closeButtonRef = useRef(null);
  const returnFocusRef = useRef(null);

  const close = useCallback(() => {
    const returnTarget = returnFocusRef.current;
    setState(null);
    returnFocusRef.current = null;
    window.requestAnimationFrame(() => returnTarget?.focus?.());
  }, []);

  const openModal = useCallback((nextState) => {
    returnFocusRef.current = nextState.returnFocusTarget || document.activeElement;
    setState(nextState);
    const analytics = {
      url: nextState.url,
      source: nextState.source || 'link',
      source_page: nextState.sourcePage || window.location.pathname,
      ...(nextState.instrument ? { instrument: nextState.instrument } : {}),
    };
    trackEvent('trial_modal_open', analytics);
    trackEvent('trial_modal_handoff', analytics);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const trials = params.getAll('trial').filter(Boolean);
    if (trials.length) {
      // Build the URL exactly as before, then fall back when the final path
      // (or any repeated trial value) resolves to a blocked slug.
      const url = resolveTrialQueryUrl(trials);
      params.delete('trial');
      const qs = params.toString();
      const clean = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
      window.history.replaceState({}, '', clean);
      if (TRIAL_SCHEDULER_MODE === 'newtab') {
        // No user gesture here, so window.open would be popup-blocked — hand
        // off the whole page to Opus1 instead (same first-party test arm).
        trackEvent('trial_newtab_open', { url, source: 'query_param' });
        window.location.assign(url);
        return;
      }
      openModal({
        url,
        title: 'Book a Trial Lesson',
        source: 'query_param',
        sourcePage: window.location.pathname,
        returnFocusTarget: null,
      });
    }
  }, [openModal]);

  useEffect(() => {
    const onClick = (e) => {
      // A component already handled this click (e.g. the instrument chooser
      // trigger / grid anchors preventDefault and run their own handoff) —
      // don't double-open the scheduler on top of it.
      if (e.defaultPrevented) return;
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
      const match = matchUrl(a.href);
      if (!match) return;
      if (match.kind === 'trial' && TRIAL_SCHEDULER_MODE === 'newtab') {
        // Retarget the anchor and let the click proceed natively: a real
        // user-gesture navigation (immune to popup blockers), and the
        // anchor's own onClick (GA4 trial_click) still fires untouched.
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        trackEvent('trial_newtab_open', { url: a.href });
        return;
      }
      e.preventDefault();
      returnFocusRef.current = a;
      setState({ url: a.href, title: match.title });
      trackEvent(match.event, {
        url: a.href,
        source: a.dataset.trialSource || 'link',
        source_page: window.location.pathname,
      });
      if (match.kind === 'trial') {
        trackEvent('trial_modal_handoff', {
          url: a.href,
          source: a.dataset.trialSource || 'link',
          source_page: window.location.pathname,
        });
      }
    };
    const onRequestOpen = (event) => {
      const detail = event.detail;
      if (!detail?.url || !matchUrl(detail.url)) return;
      event.preventDefault();
      openModal(detail);
    };
    document.addEventListener('click', onClick);
    window.addEventListener(OPEN_TRIAL_SCHEDULER_EVENT, onRequestOpen);
    return () => {
      document.removeEventListener('click', onClick);
      window.removeEventListener(OPEN_TRIAL_SCHEDULER_EVENT, onRequestOpen);
    };
  }, [openModal]);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [state, close]);

  if (!state) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-0 md:p-6"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={state.title}
    >
      <div
        className="relative w-full h-full md:h-[90vh] md:max-w-4xl md:rounded-2xl overflow-hidden bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 bg-primary text-on-primary z-10">
          <span className="font-headline text-base md:text-lg tracking-wide">{state.title}</span>
          <div className="flex items-center gap-3">
            <a
              href={state.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs uppercase tracking-wider text-on-primary/80 hover:text-on-primary underline-offset-4 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Open in new tab
            </a>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={close}
              aria-label="Close"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-on-primary transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="6" y1="18" x2="18" y2="6" />
              </svg>
            </button>
          </div>
        </div>
        <iframe
          src={state.url}
          title={state.title}
          className="w-full h-full border-0 pt-14"
          allow="payment"
        />
      </div>
    </div>
  );
}
